(function (window, document) {
	'use strict';

	var config = window.trimarkGFAddressAutocompleteConfig || {};
	var loaderPromise = null;
	var pacObserver = null;

	function getAddressInputs(root) {
		return Array.prototype.slice.call(
			(root || document).querySelectorAll('.gform_wrapper .gfield--type-address .address_line_1 input')
		);
	}

	function hasAddressFields(root) {
		return getAddressInputs(root).length > 0;
	}

	function googleAutocompleteReady() {
		return !!(
			window.google &&
			window.google.maps &&
			window.google.maps.places &&
			window.google.maps.places.Autocomplete
		);
	}

	function enhancePacContainer(container) {
		if (!container || container.dataset.trimarkPacEnhanced === '1') {
			return;
		}

		container.dataset.trimarkPacEnhanced = '1';
		container.style.zIndex = '99999';
		container.style.pointerEvents = 'auto';

		['mousedown', 'mouseup', 'click', 'touchstart', 'touchend'].forEach(function (eventName) {
			container.addEventListener(eventName, function (event) {
				event.stopPropagation();
			});
		});
	}

	function watchPacContainers() {
		Array.prototype.slice.call(document.querySelectorAll('.pac-container')).forEach(enhancePacContainer);

		if (pacObserver) {
			return;
		}

		pacObserver = new MutationObserver(function (mutations) {
			mutations.forEach(function (mutation) {
				Array.prototype.slice.call(mutation.addedNodes || []).forEach(function (node) {
					if (!node || node.nodeType !== 1) {
						return;
					}

					if (node.classList && node.classList.contains('pac-container')) {
						enhancePacContainer(node);
					}

					if (typeof node.querySelectorAll === 'function') {
						Array.prototype.slice.call(node.querySelectorAll('.pac-container')).forEach(enhancePacContainer);
					}
				});
			});
		});

		pacObserver.observe(document.body, {
			childList: true,
			subtree: true,
		});
	}

	function loadGooglePlacesFromImportLibrary() {
		if (
			!window.google ||
			!window.google.maps ||
			typeof window.google.maps.importLibrary !== 'function'
		) {
			return Promise.resolve(false);
		}

		return window.google.maps.importLibrary('places').then(function () {
			return googleAutocompleteReady();
		});
	}

	function loadGooglePlaces() {
		if (googleAutocompleteReady()) {
			return Promise.resolve();
		}

		if (loaderPromise) {
			return loaderPromise;
		}

		loaderPromise = loadGooglePlacesFromImportLibrary().then(function (ready) {
			if (ready) {
				return;
			}

			if (!config.apiKey) {
				throw new Error('Google Places API key is missing.');
			}

			return new Promise(function (resolve, reject) {
				var existingScript = document.getElementById('trimark-gf-address-autocomplete-google');
				if (existingScript) {
					existingScript.addEventListener('load', function () {
						if (googleAutocompleteReady()) {
							resolve();
						} else {
							reject(new Error('Google Places library loaded without Autocomplete support.'));
						}
					});
					existingScript.addEventListener('error', reject);
					return;
				}

				var script = document.createElement('script');
				var params = new URLSearchParams({
					key: config.apiKey,
					libraries: 'places',
					v: config.version || 'weekly',
				});

				script.id = 'trimark-gf-address-autocomplete-google';
				script.src = 'https://maps.googleapis.com/maps/api/js?' + params.toString();
				script.async = true;
				script.defer = true;
				script.onload = function () {
					if (googleAutocompleteReady()) {
						resolve();
						return;
					}

					reject(new Error('Google Places library loaded without Autocomplete support.'));
				};
				script.onerror = function () {
					reject(new Error('Failed to load Google Places library.'));
				};

				document.head.appendChild(script);
			});
		});

		return loaderPromise;
	}

	function getComponent(place, type) {
		if (!place || !Array.isArray(place.address_components)) {
			return null;
		}

		return place.address_components.find(function (component) {
			return Array.isArray(component.types) && component.types.indexOf(type) !== -1;
		}) || null;
	}

	function getLongName(place, type) {
		var component = getComponent(place, type);
		return component ? component.long_name || '' : '';
	}

	function getShortName(place, type) {
		var component = getComponent(place, type);
		return component ? component.short_name || '' : '';
	}

	function setFieldValue(field, value, dispatchEvents) {
		if (!field) {
			return;
		}

		var normalizedValue = value || '';

		if (field.tagName === 'SELECT') {
			var options = Array.prototype.slice.call(field.options || []);
			var matchedOption = options.find(function (option) {
				return option.value === normalizedValue || option.text === normalizedValue;
			});

			if (!matchedOption && normalizedValue.length === 2) {
				matchedOption = options.find(function (option) {
					return option.value.toUpperCase() === normalizedValue.toUpperCase() ||
						option.text.toUpperCase() === normalizedValue.toUpperCase();
				});
			}

			if (matchedOption) {
				field.value = matchedOption.value;
			}
		} else {
			field.value = normalizedValue;
		}

		if (dispatchEvents !== false) {
			field.dispatchEvent(new Event('input', { bubbles: true }));
			field.dispatchEvent(new Event('change', { bubbles: true }));
		}
	}

	function parsePlace(place) {
		var streetNumber = getLongName(place, 'street_number');
		var route = getLongName(place, 'route');
		var subpremise = getLongName(place, 'subpremise');
		var locality = getLongName(place, 'locality') ||
			getLongName(place, 'postal_town') ||
			getLongName(place, 'sublocality_level_1') ||
			getLongName(place, 'administrative_area_level_3');
		var postalCode = getLongName(place, 'postal_code');
		var postalCodeSuffix = getLongName(place, 'postal_code_suffix');
		var regionCode = getShortName(place, 'administrative_area_level_1');
		var regionName = getLongName(place, 'administrative_area_level_1');
		var countryCode = getShortName(place, 'country');
		var countryName = getLongName(place, 'country');
		var line1Parts = [streetNumber, route].filter(Boolean);
		var line1 = line1Parts.join(' ');
		var zip = postalCode;

		if (postalCode && postalCodeSuffix) {
			zip = postalCode + '-' + postalCodeSuffix;
		}

		if (!line1 && place && place.formatted_address) {
			line1 = place.formatted_address.split(',')[0].trim();
		}

		return {
			line1: line1,
			line2: subpremise,
			city: locality,
			stateCode: regionCode,
			stateName: regionName,
			zip: zip,
			countryCode: countryCode,
			countryName: countryName,
		};
	}

	function formatDisplayAddress(parsed) {
		var localityParts = [];
		var regionZip = [parsed.stateCode || parsed.stateName, parsed.zip].filter(Boolean).join(' ');

		if (parsed.city) {
			localityParts.push(parsed.city);
		}

		if (regionZip) {
			localityParts.push(regionZip);
		}

		return [parsed.line1, localityParts.join(', ')].filter(Boolean).join(', ');
	}

	function hidePacContainers() {
		Array.prototype.slice.call(document.querySelectorAll('.pac-container')).forEach(function (container) {
			container.style.display = 'none';
		});
	}

	function populateAddressField(fieldWrapper, place, sourceInput) {
		if (!fieldWrapper || !place) {
			return;
		}

		var parsed = parsePlace(place);
		var line1 = fieldWrapper.querySelector('.address_line_1 input');
		var line2 = fieldWrapper.querySelector('.address_line_2 input');
		var city = fieldWrapper.querySelector('.address_city input');
		var state = fieldWrapper.querySelector('.address_state select, .address_state input');
		var zip = fieldWrapper.querySelector('.address_zip input');
		var country = fieldWrapper.querySelector('.address_country select, .address_country input');
		var hasStructuredAddressFields = !!(city || state || zip || country || line2);
		var line1Value = hasStructuredAddressFields
			? parsed.line1
			: formatDisplayAddress(parsed);

		setFieldValue(line1, line1Value, false);
		setFieldValue(line2, parsed.line2);
		setFieldValue(city, parsed.city);
		setFieldValue(state, parsed.stateCode || parsed.stateName);
		setFieldValue(zip, parsed.zip);
		setFieldValue(country, parsed.countryCode || parsed.countryName);

		window.setTimeout(function () {
			if (sourceInput) {
				sourceInput.blur();
			}

			hidePacContainers();
		}, 0);
	}

	function initAddressInput(input) {
		if (!input || input.dataset.trimarkAddressAutocompleteInitialized === '1') {
			return;
		}

		var fieldWrapper = input.closest('.gfield--type-address');
		if (!fieldWrapper) {
			return;
		}

		var options = {
			fields: ['address_components', 'formatted_address', 'geometry'],
			types: ['address'],
		};

		if (Array.isArray(config.countryRestrictions) && config.countryRestrictions.length > 0) {
			options.componentRestrictions = {
				country: config.countryRestrictions,
			};
		}

		input.dataset.trimarkAddressAutocompleteInitialized = '1';
		input.setAttribute('autocomplete', 'street-address');
		input.addEventListener('focus', function () {
			Array.prototype.slice.call(document.querySelectorAll('.pac-container')).forEach(function (container) {
				container.style.display = '';
			});
		});

		var autocomplete = new window.google.maps.places.Autocomplete(input, options);
		autocomplete.addListener('place_changed', function () {
			var place = autocomplete.getPlace();
			populateAddressField(fieldWrapper, place, input);
		});
	}

	function initAddressAutocomplete(root) {
		if (!hasAddressFields(root)) {
			return;
		}

		loadGooglePlaces()
			.then(function () {
				watchPacContainers();
				getAddressInputs(root).forEach(initAddressInput);
			})
			.catch(function (error) {
				console.warn('GF address autocomplete failed to initialize.', error);
			});
	}

	function bindGravityFormsEvents() {
		if (!window.jQuery) {
			return;
		}

		window.jQuery(document).on('gform_post_render', function (event, formId) {
			var form = document.getElementById('gform_wrapper_' + formId);
			initAddressAutocomplete(form || document);
		});
	}

	document.addEventListener('DOMContentLoaded', function () {
		initAddressAutocomplete(document);
		bindGravityFormsEvents();
	});
})(window, document);
